import React, { useMemo } from 'react';
import { 
  Box, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Tooltip,
  Link,
  useTheme,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { foodItems, getFoodImagePath, getSortedFood, getAnimalData } from '../../data/foodData';
import RarityAvatar from '../common/RarityAvatar';
import RarityBadge from '../common/RarityBadge';
import { tooltipStyles, avatarStyles } from '../../theme/componentStyles';
import { tableStyles as themeTableStyles } from '../../theme/tableStyles';

export const animalTypes = Object.entries(foodItems).map(([key, value]) => ({
  id: key,
  name: value.name,
  image: key === 'fuzzies' || key === 'piggies' 
    ? `/images/beds/${key}/common/preview.png`
    : `/images/animals/${key}/common/preview.png`
}));

const FoodDatabase = ({ animalType = 'birds' }) => {
  const theme = useTheme();
  const tableStyles = useMemo(() => themeTableStyles(theme), [theme]);

  // Get the current animal data
  const animalData = getAnimalData(animalType);
  const animalName = animalData?.name || animalType;
  
  // Get sorted food items for the current animal
  const sortedFood = useMemo(() => 
    getSortedFood(animalType),
    [animalType]
  );

  // Table container styles using theme
  const containerStyle = {
    width: '100%',
    py: 0,
    px: 0,
    '& .MuiTableContainer-root': {
      ...tableStyles.tableContainer,
      borderRadius: 2,
      boxShadow: theme.shadows[3],
      backgroundColor: 'transparent',
      '&:hover': {
        boxShadow: theme.shadows[4]
      }
    },
    '& .MuiTable-root': {
      ...tableStyles.tableContainer['& .MuiTable-root'],
      backgroundColor: 'transparent',
      '&:before': {
        display: 'none'
      }
    },
    '& .MuiTableHead-root': {
      ...tableStyles.tableHead,
      '& .MuiTableRow-root': {
        '& .MuiTableCell-root': {
          ...tableStyles.tableHead['& .MuiTableCell-head'],
          height: '40px',
          minHeight: '40px',
          maxHeight: '40px',
          textAlign: 'center',
          '&:first-of-type': {
            borderLeft: `1px solid ${theme.palette.primary.dark}`,
            borderTopLeftRadius: '4px'
          },
          '&:last-child': {
            borderRight: `1px solid ${theme.palette.primary.dark}`,
            borderTopRightRadius: '4px'
          }
        }
      }
    },
    '& .MuiTableBody-root': {
      ...tableStyles.tableBody,
      '& .MuiTableRow-root': {
        height: 59,
        minHeight: 59,
        maxHeight: 59,
        backgroundColor: 'transparent',
        '&:hover': {
          backgroundColor: theme.palette.action.hover
        },
        '&:last-child': {
          '& .MuiTableCell-root': {
            borderBottom: 'none',
            '&:first-of-type': {
              borderBottomLeftRadius: '4px'
            },
            '&:last-child': {
              borderBottomRightRadius: '4px'
            }
          }
        },
        '& .MuiTableCell-root': {
          ...tableStyles.tableBody['& .MuiTableRow-root']['& .MuiTableCell-root'],
          padding: '8px 16px',
          '&:first-of-type': {
            borderLeft: `1px solid ${theme.palette.divider}`
          },
          '&:last-child': {
            borderRight: `1px solid ${theme.palette.divider}`
          }
        }
      }
    }
  };

  return (
    <Box sx={containerStyle}>
      <TableContainer>
        <Table size="small" aria-label="food database">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '25%', textAlign: 'center', backgroundColor: 'transparent' }}>Food</TableCell>
              <TableCell sx={{ width: '15%', textAlign: 'center', backgroundColor: 'transparent' }}>Rarity</TableCell>
              <TableCell sx={{ width: '35%', textAlign: 'center', backgroundColor: 'transparent' }}>Requirements</TableCell>
              <TableCell sx={{ width: '25%', textAlign: 'center', backgroundColor: 'transparent' }}>Animal</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedFood && sortedFood.length > 0 ? sortedFood.map((item, index) => (
              <TableRow 
                key={index} 
                hover 
                sx={{ 
                  backgroundColor: 'transparent',
                  '&:hover': { 
                    backgroundColor: 'action.hover',
                    '& td': {
                      bgcolor: 'action.hover',
                    }
                  },
                  '&:last-child td': {
                    borderBottom: 0,
                  }
                }}
              >
                <TableCell sx={{ textAlign: 'center', verticalAlign: 'middle', px: 3, backgroundColor: 'transparent' }}>
                  <RarityAvatar 
                    src={getFoodImagePath(animalType, item.rarity.toLowerCase())}
                    alt={`${item.name} (${item.rarity})`}
                    size={60}
                    sx={{
                      ...avatarStyles.standard,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      margin: '0 auto',
                    }}
                  />
                </TableCell>
                <TableCell sx={{ 
                  textAlign: 'center',
                  verticalAlign: 'middle',
                  padding: '8px 16px',
                  height: '59px',
                  display: 'table-cell',
                  backgroundColor: 'transparent'
                }}>
                  <Box sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    width: '100%'
                  }}>
                    <RarityBadge 
                      rarity={item.rarity}
                      size="small"
                      sx={{ 
                        '& .MuiChip-label': { 
                          px: 1.5,
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          lineHeight: 1.5,
                        },
                        minWidth: 80,
                        height: 24,
                        borderRadius: 1
                      }}
                    />
                  </Box>
                </TableCell>
                <TableCell sx={{ textAlign: 'center', padding: '8px 16px', backgroundColor: 'transparent' }}>
                  <Box sx={{ 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    gap: 1, 
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: 40
                  }}>
                    {item.seeds && item.seeds.map((seed, idx) => {
                      const seedName = seed.name.replace(/\.$/, '');
                      return (
                        <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Tooltip 
                            title={`${seedName} (${seed.rarity})`}
                            arrow
                            componentsProps={{
                              tooltip: tooltipStyles.tooltip,
                              arrow: tooltipStyles.arrow,
                            }}
                          >
                            <Box>
                              <RarityAvatar 
                                src={getFoodImagePath('', seed.rarity, seedName)}
                                alt={`${seedName} (${seed.rarity})`}
                                size={40}
                                sx={[avatarStyles.root, avatarStyles.hover]}
                                aria-label={`${seedName} (${seed.rarity})`}
                              />
                            </Box>
                          </Tooltip>
                          <Typography variant="body2" sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
                            ×{seed.amount}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </TableCell>
                <TableCell sx={{ textAlign: 'center', verticalAlign: 'middle', backgroundColor: 'transparent' }}>
                  <Link 
                    component={RouterLink} 
                    to={`/animals#${animalType}`}
                    sx={{ 
                      textDecoration: 'none',
                      color: 'inherit',
                    }}
                  >
                    <RarityAvatar 
                      src={animalType === 'fuzzies' || animalType === 'piggies'
                        ? `/images/beds/${animalType}/common/preview.png`
                        : `/images/animals/${animalType}/common/preview.png`
                      }
                      alt={animalData.name}
                      size={60}
                      sx={{
                        ...avatarStyles.standard,
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        transition: 'transform 0.2s',
                        '&:hover': {
                          transform: 'scale(1.05)',
                        },
                        margin: '0 auto'
                      }}
                    />
                  </Link>
                </TableCell>
              </TableRow>
            )) : (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4, backgroundColor: 'transparent' }}>
                  <Typography variant="body2" color="text.secondary">
                    No food data available for {animalName}.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default FoodDatabase;
